import { ReactElement } from 'react'
import BorderInner from '@mui/icons-material/BorderInner'
import { Box, InputAdornment, Stack, Switch, TextField, Typography } from '@mui/material'
import { useCameraStore } from '@modugen/ui-toolkit'
import { ToggleButtonDropDown } from '@ui/actions'
import {
  useControlStore,
  useModelStore,
  usePlanImageCardStore,
  usePlanTableStore,
} from '@editorStores'

const DrawControls = (): ReactElement | null => {
  const showComments = useModelStore(state => state.showComments)
  const showPlanTables = usePlanTableStore(state => state.isVisible)
  const showPlanImageCards = usePlanImageCardStore(state => state.isVisible)
  const showCommentCategorySnap = showComments || showPlanTables || showPlanImageCards
  const snappingFeaturesEnabled = useControlStore(state => state.snappingFeaturesEnabled)
  const snapToCornersAndEdges = useControlStore(state => state.snapToCornersAndEdges)
  const snapOrthogonal = useControlStore(state => state.snapOrthogonal)
  const snapToTapelineCenters = useControlStore(state => state.snapToTapelineCenters)
  const snapToComments = useControlStore(state => state.snapToComments)
  const snapToAngles = useControlStore(state => state.snapToAngles)
  const snapAngle = useControlStore(state => state.snapAngle)
  const setSnapToCornersAndEdges = useControlStore(state => state.setSnapToCornersAndEdges)
  const setSnapOrthogonal = useControlStore(state => state.setSnapOrthogonal)
  const setSnapToTapelineCenters = useControlStore(state => state.setSnapToTapelineCenters)
  const setSnapToComments = useControlStore(state => state.setSnapToComments)
  const setSnapToAngles = useControlStore(state => state.setSnapToAngles)
  const setSnapAngle = useControlStore(state => state.setSnapAngle)

  const enableXYFeaturesInOrthographic = useCameraStore(
    state => state.enableXYFeaturesInOrthographic,
  )

  return (
    <Box>
      <ToggleButtonDropDown
        Icon={BorderInner}
        iconSize="small"
        extendTo="right"
        hint="Einstellungen für Snapping"
        data-cy="btn-draw-controls"
      >
        {snappingFeaturesEnabled.snapToCornersAndEdges && (
          <Stack direction="row" alignItems="center">
            <Switch
              disabled={!enableXYFeaturesInOrthographic}
              checked={snapToCornersAndEdges}
              onChange={event => {
                setSnapToCornersAndEdges(event.target.checked)
              }}
              size="small"
            />
            <Typography sx={{ p: 1 }} noWrap>
              an Elementen ausrichten
            </Typography>
          </Stack>
        )}

        {snappingFeaturesEnabled.snapOrthogonal && (
          <Stack direction="row" alignItems="center">
            <Switch
              disabled={!enableXYFeaturesInOrthographic}
              checked={snapOrthogonal}
              onChange={event => {
                setSnapOrthogonal(event.target.checked)
              }}
              size="small"
              data-cy="btn-snap-orthogonal"
            />
            <Typography sx={{ p: 1 }} noWrap>
              orthogonal an Elementen ausrichten
            </Typography>
          </Stack>
        )}

        {snappingFeaturesEnabled.snapToAngle && (
          <Stack direction="row" alignItems="center">
            <Switch
              checked={snapToAngles}
              onChange={event => {
                setSnapToAngles(event.target.checked)
              }}
              size="small"
            />
            <Typography sx={{ p: 1 }} noWrap>
              an Winkel ausrichten
            </Typography>
            <TextField
              type="number"
              size="small"
              value={snapAngle}
              onChange={event => {
                const parsed = Number.parseInt(event.target.value, 10)
                if (Number.isNaN(parsed)) return
                setSnapAngle(Math.min(180, Math.max(1, parsed)))
              }}
              inputProps={{ min: 1, max: 180, step: 1 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">°</InputAdornment>,
              }}
              data-cy="input-snap-angle"
            />
          </Stack>
        )}
        {snappingFeaturesEnabled.snapToTapelineCenters && (
          <Stack direction="row" alignItems="center">
            <Switch
              checked={snapToTapelineCenters}
              onChange={event => {
                setSnapToTapelineCenters(event.target.checked)
              }}
              size="small"
            />
            <Typography sx={{ p: 1 }} noWrap>
              an Mittelpunkten von Maßlinien ausrichten
            </Typography>
          </Stack>
        )}
        {snappingFeaturesEnabled.snapToComments && showCommentCategorySnap && (
          <Stack direction="row" alignItems="center">
            <Switch
              checked={snapToComments}
              onChange={event => {
                setSnapToComments(event.target.checked)
              }}
              size="small"
            />
            <Typography sx={{ p: 1 }} noWrap>
              an Kommentaren, Tabellen und Bildern ausrichten
            </Typography>
          </Stack>
        )}
      </ToggleButtonDropDown>
    </Box>
  )
}

export default DrawControls
